/**
 * The sidebar, HUD, and minimap (U16).
 *
 * Four tabs on the number row, leaving W/A/S/D free to pan. Each icon shows its
 * name and cost, a progress fill while building, a queue-count badge, and a
 * dimmed state when unaffordable.
 *
 * Anything whose prerequisites are unmet is shown **locked rather than hidden**.
 * It used to be omitted outright, on the reasoning that an icon you cannot
 * click is noise. That is true for someone who knows the tech tree and wrong
 * for everyone else: a new player has no way to discover that a Prism Tank
 * exists, let alone that three buildings stand between them and it. Hovering a
 * locked icon says exactly what is missing, in order.
 *
 * Every rejected action names its reason on the alert line (R33). R11's
 * deliberate no-snapping choice only reads as a rule rather than as an
 * unresponsive game if the player is told why.
 *
 * Ownership and state never rest on hue alone (R34): the power deficit carries
 * a `LOW POWER` label and a hatched fill, offline defences raise a persistent
 * banner, and minimap blips use the per-slot owner ramp rather than a faction
 * colour.
 */

import type { Ctx, Subsystem } from '../core/registry.js'
import * as fix from '../core/fixed.js'
import { MAP_SIZE, MAX_QUEUE_DEPTH } from '../core/config.js'
import { Entities, EF } from '../sim/entities.js'
import { describe as describeEntity } from './describe.js'
import { World, CELL, TERRAIN, GROUND } from '../sim/world.js'
import { Economy } from '../sim/economy.js'
import { Build } from '../sim/build.js'
import { Vision } from '../sim/vision.js'
import { Renderer } from '../render/index.js'
import { Selection } from '../input/select.js'
import { ENTITY_DEFS, TAB, TAB_NAMES, ROLE, defById, type EntityDef } from '../sim/data/entities.js'
import { IconForge } from './icons.js'
import { REASON, REASON_TEXT, type Reason } from '../net/commands.js'

const TAB_KEYS = ['1', '2', '3', '4']
const MINIMAP_SIZE = 128
/** Selection entries shown before an overflow indicator takes over. */
const SELECTION_CAP = 8

/**
 * Selection rows group by `kind * GROUP_STRIDE + passengerKind`, so a loaded
 * transport gets its own row. Comfortably above every entity id.
 */
const GROUP_STRIDE = 1024

/** The queue cap is the one reason whose wording carries a number. */
const QUEUE_FULL_TEXT = `Queue full (${MAX_QUEUE_DEPTH})`

export interface SidebarCallbacks {
  onQueue(tab: number, kind: number): void
  onCancel(tab: number, index: number): void
  /** Entering placement mode for a structure awaiting a footprint. */
  onPlaceRequest(kind: number): void
  onSuperweaponRequest(): void
  onMinimapClick(cellX: number, cellY: number): void
  /** The music button, or the M key. */
  onToggleMusic(): void
  /** The CRT button, or the V key. */
  /** The speed button, or the + key. */
  onCycleSpeed(): void
  /** T — repair the selected structures. */
  onRepair(): void
  /** Shift+Z — sell them. */
  onSell(): void
  /** Deploy a founder or put a transport's passenger down. */
  onDeploy(): void
}

export class Sidebar implements Subsystem {
  static id = 'ui'
  static deps = ['world', 'entities', 'economy', 'build', 'vision', 'render', 'selection'] as const

  private world!: World
  private entities!: Entities
  private economy!: Economy
  private build!: Build
  private vision!: Vision
  private renderer!: Renderer
  private selection!: Selection

  private icons!: IconForge
  localSlot = 0
  callbacks: SidebarCallbacks | null = null

  private activeTab: number = TAB.Structure
  /** What the selection panel was last built for; '' means empty. */
  private selectionKey = ''
  private readonly selectionRows = new Map<number, HTMLDivElement>()
  private repairButton: HTMLButtonElement | null = null

  // --- DOM ------------------------------------------------------------------
  private readonly root: HTMLElement
  private el!: {
    sidebar: HTMLDivElement
    credits: HTMLSpanElement
    power: HTMLDivElement
    powerFill: HTMLDivElement
    powerText: HTMLSpanElement
    powerState: HTMLSpanElement
    tabs: HTMLButtonElement[]
    icons: HTMLDivElement
    minimap: HTMLCanvasElement
    musicButton: HTMLButtonElement
    speedButton: HTMLButtonElement
    superRow: HTMLDivElement
    superFill: HTMLDivElement
    superButton: HTMLButtonElement
    selection: HTMLDivElement
    tooltip: HTMLDivElement
    alerts: HTMLDivElement
    offline: HTMLDivElement
  }

  private readonly iconNodes = new Map<number, HTMLButtonElement>()
  private minimapCtx: CanvasRenderingContext2D | null = null
  private displayedCredits = 0

  constructor(root: HTMLElement) {
    this.root = root
  }

  init(ctx: Ctx): void {
    this.world = ctx.get<World>('world')
    this.entities = ctx.get<Entities>('entities')
    this.economy = ctx.get<Economy>('economy')
    this.build = ctx.get<Build>('build')
    this.vision = ctx.get<Vision>('vision')
    this.renderer = ctx.get<Renderer>('render')
    this.selection = ctx.get<Selection>('selection')

    this.icons = new IconForge(this.renderer.webglRenderer, this.renderer.palette, this.renderer)
    this.buildDom()

    // Every alert below is filtered to the local player.
    //
    // The simulation broadcasts these for *all* players, including the AI, and
    // the handlers used to ignore the `owner` the events carry. The result was
    // being told to go and place the enemy's Fusion Core — a building the
    // player cannot place, of a faction they may not even be playing.
    //
    // Rejections that carry no owner come from the local input layer, which
    // only ever speaks for this player, so those are shown unconditionally.
    ctx.on('order:rejected', (p) => {
      const q = p as { owner?: number; reason: Reason; detail?: string }
      if (q.owner !== undefined && q.owner !== this.localSlot) return
      this.onRejected(q)
    })
    ctx.on('build:ready-to-place', (p) => {
      const q = p as { owner: number; kind: number }
      if (q.owner !== this.localSlot) return
      this.alert(`${defById(q.kind)?.name ?? 'Structure'} ready — place it`)
    })
    ctx.on('super:ready', (p) => {
      if ((p as { owner: number }).owner !== this.localSlot) return
      this.alert('Superweapon ready')
    })
    ctx.on('match:setup', () =>
      this.alert('Select your Mobile Command Vehicle and press D to deploy'),
    )
    ctx.on('match:over', (p) => {
      const q = p as { winner: number; reason: string }
      this.alert(q.reason === 'win' ? `Player ${q.winner} wins` : 'Match stalled', true)
    })
  }

  setTab(tab: number): void {
    this.activeTab = tab
    for (let i = 0; i < this.el.tabs.length; i++) {
      this.el.tabs[i].classList.toggle('active', i === tab)
    }
    this.rebuildIcons()
  }

  get tab(): number {
    return this.activeTab
  }

  // --- construction ---------------------------------------------------------

  private buildDom(): void {
    const sidebar = div('ba-sidebar')
    const brand = div('ba-brand')
    brand.innerHTML = 'BLUE <span>ALERT</span>'

    const credits = document.createElement('div')
    credits.className = 'ba-credits'
    const creditValue = document.createElement('span')
    creditValue.textContent = '0'
    const creditLabel = document.createElement('span')
    creditLabel.className = 'label'
    creditLabel.textContent = 'CREDITS'
    credits.append(creditLabel, creditValue)

    const power = div('ba-power')
    const powerBar = div('ba-power-bar')
    const powerFill = div('ba-power-fill')
    powerBar.append(powerFill)
    const powerLabel = div('ba-power-label')
    const powerText = document.createElement('span')
    const powerState = document.createElement('span')
    powerState.className = 'state'
    powerLabel.append(powerText, powerState)
    power.append(powerBar, powerLabel)

    const tabsRow = div('ba-tabs')
    const tabs: HTMLButtonElement[] = []
    for (let i = 0; i < 4; i++) {
      const b = document.createElement('button')
      b.className = 'ba-tab'
      b.innerHTML = `${TAB_NAMES[i].toUpperCase()}<span class="key">${TAB_KEYS[i]}</span>`
      b.addEventListener('click', () => this.setTab(i))
      tabsRow.append(b)
      tabs.push(b)
    }

    const icons = div('ba-icons')

    const superRow = div('ba-super')
    const superBar = div('ba-super-bar')
    const superFill = div('ba-super-fill')
    superBar.append(superFill)
    const superButton = document.createElement('button')
    superButton.textContent = 'SUPERWEAPON'
    superButton.addEventListener('click', () => {
      if (this.build.isSuperweaponReady(this.localSlot)) this.callbacks?.onSuperweaponRequest()
    })
    superRow.append(superBar, superButton)

    const minimapWrap = div('ba-minimap')
    const minimap = document.createElement('canvas')
    minimap.width = MINIMAP_SIZE
    minimap.height = MINIMAP_SIZE
    minimapWrap.title = 'Tactical map'
    minimapWrap.append(minimap)
    let mapPointer: number | null = null
    const panMinimap = (e: PointerEvent): void => {
      const rect = minimap.getBoundingClientRect()
      const cx = Math.max(0, Math.min(MAP_SIZE - 1, Math.floor(((e.clientX - rect.left) / rect.width) * MAP_SIZE)))
      const cy = Math.max(0, Math.min(MAP_SIZE - 1, Math.floor(((e.clientY - rect.top) / rect.height) * MAP_SIZE)))
      this.callbacks?.onMinimapClick(cx, cy)
    }
    minimapWrap.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || mapPointer !== null) return
      e.preventDefault()
      mapPointer = e.pointerId
      minimapWrap.setPointerCapture(e.pointerId)
      panMinimap(e)
    })
    minimapWrap.addEventListener('pointermove', (e) => {
      if (e.pointerId === mapPointer) panMinimap(e)
    })
    minimapWrap.addEventListener('pointerup', (e) => {
      if (e.pointerId !== mapPointer) return
      mapPointer = null
      minimapWrap.releasePointerCapture(e.pointerId)
    })
    minimapWrap.addEventListener('lostpointercapture', () => { mapPointer = null })
    minimapWrap.addEventListener('pointercancel', () => { mapPointer = null })
    this.minimapCtx = minimap.getContext('2d')

    // Options: one row, because there is exactly one thing to toggle and a
    // settings panel for it would be more chrome than setting.
    const options = div('ba-options')
    const musicButton = document.createElement('button')
    musicButton.className = 'ba-toggle'
    musicButton.textContent = 'MUSIC ON'
    musicButton.title = 'Toggle music (M)'
    musicButton.addEventListener('click', () => this.callbacks?.onToggleMusic())
    const speedButton = document.createElement('button')
    speedButton.className = 'ba-toggle'
    speedButton.textContent = 'SPEED 1x'
    speedButton.title = 'Game speed (+)'
    speedButton.addEventListener('click', () => this.callbacks?.onCycleSpeed())
    options.append(musicButton, speedButton)

    const selectionPanel = div('ba-selection')

    // Radar high, build list below it, and nothing important against the bottom
    // edge of the window.
    //
    // The minimap, the options row and the selection panel used to be the last three
    // children of a full-height column, which put the repair and sell buttons a few
    // pixels above the bottom of the viewport. In a browser that is where the window
    // resize grabber lives, so aiming at Repair in Safari frequently resized the
    // window instead. Moving the radar to the top lifts everything under it by the
    // radar's own height, and `.ba-sidebar` keeps a reserved strip below the last
    // control so the two targets never coincide again.
    // Selection above the superweapon bar and the options row rather than
    // directly on top of them.
    //
    // Everything after the icon grid is pinned to the bottom of the sidebar,
    // and the repair and sell buttons live in this panel — so sitting last put
    // the two buttons that destroy a building right against the screen edge,
    // which is a long reach and an easy overshoot.
    sidebar.append(brand, credits, power, minimapWrap, tabsRow, icons, selectionPanel, superRow, options)

    // The hover panel lives outside the sidebar so it can overhang the world
    // without being clipped by the sidebar's own overflow.
    const tooltip = div('ba-tip')

    const alerts = div('ba-alerts')
    const offline = div('ba-offline')
    offline.textContent = 'DEFENCES OFFLINE — LOW POWER'

    this.root.append(sidebar, alerts, offline, tooltip)

    this.el = {
      sidebar,
      credits: creditValue,
      power,
      powerFill,
      powerText,
      powerState,
      tabs,
      icons,
      minimap,
      musicButton,
      speedButton,
      superRow,
      superFill,
      superButton,
      selection: selectionPanel,
      tooltip,
      alerts,
      offline,
    }
    this.setTab(TAB.Structure)
  }

  // --- per-frame ------------------------------------------------------------

  update(): void {
    this.updateCredits()
    this.updatePower()
    this.updateIcons()
    this.updateSuperweapon()
    this.updateSelection()
    this.drawMinimap()
  }

  private updateCredits(): void {
    const actual = this.economy.creditsOf(this.localSlot)
    // A ticking counter, as Red Alert does: the number chases the real value
    // rather than snapping, which makes income legible at a glance.
    const delta = actual - this.displayedCredits
    if (delta !== 0) {
      const step = Math.max(1, Math.ceil(Math.abs(delta) / 12))
      this.displayedCredits += delta > 0 ? Math.min(step, delta) : Math.max(-step, delta)
    }
    this.el.credits.textContent = String(this.displayedCredits)
  }

  private updatePower(): void {
    const produced = this.economy.powerProduced[this.localSlot]
    const consumed = this.economy.powerConsumed[this.localSlot]
    const factor = fix.toFloat(this.economy.factorOf(this.localSlot))
    const ratio = consumed === 0 ? 1 : Math.min(1, produced / consumed)

    this.el.powerFill.style.width = `${Math.round(ratio * 100)}%`
    this.el.powerText.textContent = `${produced} / ${consumed}`
    const deficit = factor < 1
    this.el.power.classList.toggle('deficit', deficit)
    this.el.powerState.textContent = deficit ? 'LOW POWER' : ''

    // A persistent banner while any defence is offline: otherwise the player
    // watches a base fall while its turrets look armed.
    let offline = false
    for (let s = 0; s < this.entities.limit && !offline; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== this.localSlot) continue
      if (this.entities.has(s, EF.Offline)) offline = true
    }
    this.el.offline.classList.toggle('visible', offline)
  }

  /** Icons for the active tab, filtered to what prerequisites allow. */
  private rebuildIcons(): void {
    this.el.icons.replaceChildren()
    this.iconNodes.clear()

    for (const def of this.availableDefs()) {
      const node = document.createElement('button')
      node.className = 'ba-icon interactive'
      node.append(this.icons.iconFor(def.id, this.localSlot))
      node.addEventListener('pointerenter', () => this.showTooltip(def, node))
      node.addEventListener('pointerleave', () => this.hideTooltip())

      const label = div('label')
      const name = document.createElement('span')
      name.className = 'name'
      name.textContent = def.name
      const cost = document.createElement('span')
      cost.className = 'cost'
      cost.textContent = String(def.cost)
      label.append(name, cost)

      const progress = div('progress')
      const status = document.createElement('span')
      status.className = 'build-status'
      const count = document.createElement('span')
      count.className = 'count'
      count.style.display = 'none'
      node.append(progress, status, count, label)

      node.addEventListener('click', () => {
        // A locked icon is a label, not a button.
        if (!this.build.prereqsMet(this.localSlot, def)) return
        if (this.build.isReadyToPlace(this.localSlot, def.id)) {
          this.callbacks?.onPlaceRequest(def.id)
          return
        }
        this.callbacks?.onQueue(def.tab, def.id)
      })
      // Right-click cancels, as Red Alert does.
      node.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        e.stopPropagation()
        const index = this.queueIndexOf(def)
        if (index >= 0) this.callbacks?.onCancel(def.tab, index)
      })

      this.el.icons.append(node)
      this.iconNodes.set(def.id, node)
    }
  }

  /** Definitions the player could see in this tab right now. */
  /**
   * Everything this faction can ever build in this tab, buildable or not.
   *
   * Locked entries used to be omitted entirely, which hides the tech tree from
   * the only person who needs to see it: someone who has never played this and
   * has no idea that a Prism Tank exists, let alone that three buildings stand
   * between them and it. They are listed and greyed, and the tooltip says what
   * is missing.
   */
  private availableDefs(): EntityDef[] {
    const faction = this.factionOfLocal()
    return ENTITY_DEFS.filter(
      (d) => d.tab === this.activeTab && d.faction === faction && d.role !== ROLE.BaseRoot,
    )
  }

  /** Whether the player has a finished structure of this kind. */
  private owns(kind: number): boolean {
    for (let s = 0; s < this.entities.limit; s++) {
      if (!this.entities.isAlive(s)) continue
      if (this.entities.owner[s] !== this.localSlot) continue
      if (this.entities.has(s, EF.Building)) continue
      if (this.entities.kind[s] === kind) return true
    }
    return false
  }

  /** Fill and show the hover panel for one entity. */
  private showTooltip(def: EntityDef, anchor: HTMLElement): void {
    const d = describeEntity(def, (kind) => this.owns(kind))
    const tip = this.el.tooltip
    tip.replaceChildren()

    const title = div('tip-title')
    title.textContent = d.name
    tip.append(title)

    if (d.summary) {
      const summary = div('tip-summary')
      summary.textContent = d.summary
      tip.append(summary)
    }

    const grid = div('tip-stats')
    for (const stat of d.stats) {
      const key = document.createElement('span')
      key.className = 'k'
      key.textContent = stat.label
      const value = document.createElement('span')
      value.className = 'v'
      value.textContent = stat.value
      grid.append(key, value)
    }
    tip.append(grid)

    const line = (className: string, text: string): void => {
      const node = div(className)
      node.textContent = text
      tip.append(node)
    }
    if (d.strong.length) line('tip-good', `Strong vs ${d.strong.join(', ')}`)
    if (d.weak.length) line('tip-bad', `Weak vs ${d.weak.join(', ')}`)
    if (d.unlocks.length) line('tip-unlock', `Unlocks ${d.unlocks.join(', ')}`)
    if (d.needs.length) line('tip-need', `Requires ${d.needs.join(', then ')}`)

    // Anchored to the icon, clamped so a tall panel near the bottom of a short
    // window is not cut off by the viewport.
    tip.classList.add('visible')
    const rect = anchor.getBoundingClientRect()
    const top = Math.min(rect.top, window.innerHeight - tip.offsetHeight - 8)
    tip.style.top = `${Math.max(8, top)}px`
    tip.style.right = `${window.innerWidth - rect.left + 8}px`
  }

  private hideTooltip(): void {
    this.el.tooltip.classList.remove('visible')
  }

  private factionOfLocal(): number {
    for (let s = 0; s < this.entities.limit; s++) {
      if (!this.entities.isAlive(s) || this.entities.owner[s] !== this.localSlot) continue
      const def = defById(this.entities.kind[s])
      if (def) return def.faction
    }
    return 0
  }

  private queueIndexOf(def: EntityDef): number {
    for (let i = 0; i < this.build.queueDepth(this.localSlot, def.tab); i++) {
      if (this.build.queueKindAt(this.localSlot, def.tab, i) === def.id) return i
    }
    return -1
  }

  private updateIcons(): void {
    // The available set changes as structures rise and fall, so it is compared
    // rather than rebuilt every frame — replacing the DOM each frame would
    // cancel a click mid-press.
    const wanted = this.availableDefs()
    if (wanted.length !== this.iconNodes.size || wanted.some((d) => !this.iconNodes.has(d.id))) {
      this.rebuildIcons()
      return
    }

    const credits = this.economy.creditsOf(this.localSlot)
    for (const def of wanted) {
      const node = this.iconNodes.get(def.id)
      if (!node) continue

      // `canBuild`, not `prereqsMet`: the two disagree exactly when the tech
      // tree allows a unit but nothing exists to build it in — a harvester
      // with a refinery up but no Foundry — and showing it lit invites a click
      // the simulation will refuse.
      const unlocked = this.build.canBuild(this.localSlot, def.id)
      node.classList.toggle('locked', !unlocked)
      const ready = unlocked && this.build.isReadyToPlace(this.localSlot, def.id)
      node.classList.toggle('ready', ready)
      node.classList.toggle('unaffordable', unlocked && !ready && credits < def.cost)

      let queued = 0
      let progress = 0
      let building = false
      for (let i = 0; i < this.build.queueDepth(this.localSlot, def.tab); i++) {
        if (this.build.queueKindAt(this.localSlot, def.tab, i) !== def.id) continue
        queued++
        if (i === 0 && !ready) {
          building = true
          progress = fix.toFloat(this.build.queueProgressAt(this.localSlot, def.tab, 0))
        }
      }

      node.classList.toggle('building', building)
      const progressEl = node.querySelector('.progress') as HTMLDivElement
      progressEl.style.width = `${ready ? 100 : Math.min(100, progress * 100)}%`
      const statusEl = node.querySelector('.build-status') as HTMLSpanElement
      statusEl.textContent = ready ? 'READY' : building ? `${Math.floor(progress * 100)}%` : queued > 0 ? 'QUEUED' : ''
      const countEl = node.querySelector('.count') as HTMLSpanElement
      countEl.style.display = queued > 0 ? 'block' : 'none'
      countEl.textContent = String(queued)
    }
  }

  private updateSuperweapon(): void {
    const kind = this.build.superKind[this.localSlot]
    const has = kind !== 0 && this.build.hasStructure(this.localSlot, kind)
    this.el.superRow.classList.toggle('visible', has)
    if (!has) return

    const progress = fix.toFloat(this.build.superweaponProgress(this.localSlot))
    const ready = this.build.isSuperweaponReady(this.localSlot)
    this.el.superFill.style.width = `${Math.round(Math.min(1, progress) * 100)}%`
    this.el.superRow.classList.toggle('ready', ready)
    this.el.superButton.disabled = !ready
    this.el.superButton.textContent = ready
      ? (defById(kind)?.name ?? 'SUPERWEAPON').toUpperCase()
      : `CHARGING ${Math.floor(progress * 100)}%`
  }

  /**
   * Grouped by type with a count badge, capped, with an overflow indicator.
   *
   * **Rebuilt only when the shape of the selection changes**, never per frame.
   *
   * This used to call `replaceChildren` on every update, which is sixty times a
   * second. The rows looked fine, because they are only ever read — but the
   * repair and sell buttons are *clicked*, and a button that is destroyed and
   * recreated between pointerdown and pointerup never fires a click at all. The
   * panel looked completely correct and the buttons did nothing.
   *
   * So the DOM is keyed on a signature of what is selected, and everything that
   * changes continuously — health, repair state — is written into the existing
   * nodes in place.
   */
  private updateSelection(): void {
    const ids = this.selection.selected
    if (ids.length === 0) {
      if (this.selectionKey !== '') {
        this.selectionKey = ''
        this.selectionRows.clear()
        this.el.selection.replaceChildren()
      }
      return
    }

    // Grouped by kind *and* passenger, so a loaded Outrider is its own row —
    // "Outrider (Marksman)" — rather than blending into the empty ones. The
    // passenger was completely invisible here, which made the one unit built
    // around who is inside it read as a unit carrying nobody.
    const groups = new Map<number, { count: number; hp: number; maxHp: number }>()
    let structures = 0
    let repairing = 0
    let loaded = 0
    let founders = 0
    for (const id of ids) {
      const slot = this.entities.resolve(id)
      if (slot < 0) continue
      if (this.entities.has(slot, EF.Structure)) {
        structures++
        if (this.entities.has(slot, EF.Repairing)) repairing++
      }
      const passenger = this.entities.passenger[slot]
      if (passenger !== 0) loaded++
      if (this.entities.has(slot, EF.Founder)) founders++
      const kind = this.entities.kind[slot] * GROUP_STRIDE + passenger
      const g = groups.get(kind) ?? { count: 0, hp: 0, maxHp: 0 }
      g.count++
      g.hp += this.entities.hp[slot]
      g.maxHp += this.entities.maxHp[slot]
      groups.set(kind, g)
    }

    // Ascending kind so the panel does not reshuffle between identical
    // selections made in a different order.
    const kinds = [...groups.keys()].sort((a, b) => a - b)
    const key = `${kinds.map((k) => `${k}x${groups.get(k)!.count}`).join(',')}|${structures > 0}|${loaded > 0}`

    if (key !== this.selectionKey) {
      this.selectionKey = key
      this.selectionRows.clear()
      const rows: HTMLElement[] = []
      for (const kind of kinds.slice(0, SELECTION_CAP)) {
        const g = groups.get(kind)!
        const baseKind = (kind / GROUP_STRIDE) | 0
        const passenger = kind % GROUP_STRIDE
        const row = div('row')
        const name = document.createElement('span')
        const baseName = defById(baseKind)?.name ?? `#${baseKind}`
        name.textContent =
          passenger !== 0 ? `${baseName} (${defById(passenger)?.name ?? '?'})` : baseName
        const count = document.createElement('span')
        count.textContent = g.count > 1 ? `×${g.count}` : ''
        row.append(name, count)

        const hp = div('hp')
        const fill = document.createElement('div')
        hp.append(fill)
        this.selectionRows.set(kind, fill)

        const wrap = document.createElement('div')
        wrap.append(row, hp)
        rows.push(wrap)
      }
      if (kinds.length > SELECTION_CAP) {
        const more = div('overflow')
        more.textContent = `+${kinds.length - SELECTION_CAP} more types`
        rows.push(more)
      }

      // Repair and sell, shown only when a structure is selected. Both have key
      // bindings, but neither is guessable, and a player who never finds them is
      // playing a game where damaged buildings simply stay damaged.
      if (structures > 0) {
        const actions = div('ba-structure-actions')
        const repair = document.createElement('button')
        repair.className = 'ba-toggle'
        repair.title = 'Repair the selected structures (K)'
        repair.addEventListener('click', () => this.callbacks?.onRepair())
        const sell = document.createElement('button')
        sell.className = 'ba-toggle danger'
        sell.textContent = 'SELL'
        sell.title = 'Sell the selected structures for half their value (L)'
        sell.addEventListener('click', () => this.callbacks?.onSell())
        actions.append(repair, sell)
        rows.push(actions)
        this.repairButton = repair
      } else {
        this.repairButton = null
      }

      // Unload, shown only when a loaded transport is selected. It shares the
      // D binding with deploy and dig-in, and like repair and sell the binding
      // is not guessable — without this button the passenger can be put in
      // and never taken out.
      if (loaded > 0 || founders > 0) {
        const actions = div('ba-structure-actions')
        const unload = document.createElement('button')
        unload.className = 'ba-toggle'
        unload.textContent = founders > 0 ? 'DEPLOY' : 'UNLOAD'
        unload.title = founders > 0 ? 'Deploy a Command Yard (D)' : 'Put the passenger down beside the vehicle (D)'
        unload.addEventListener('click', () => this.callbacks?.onDeploy())
        actions.append(unload)
        rows.push(actions)
      }

      this.el.selection.replaceChildren(...rows)
    }

    // Live values, written into the nodes that already exist.
    for (const [kind, fill] of this.selectionRows) {
      const g = groups.get(kind)
      if (!g) continue
      fill.style.width = `${Math.round((g.hp / Math.max(1, g.maxHp)) * 100)}%`
    }
    if (this.repairButton) {
      this.repairButton.textContent = repairing > 0 ? 'REPAIRING' : 'REPAIR'
      this.repairButton.classList.toggle('active', repairing > 0)
    }
  }

  /** Reflect the music state. Called by whoever owns the switch. */
  setMusicEnabled(on: boolean): void {
    if (!this.el) return
    this.el.musicButton.textContent = on ? 'MUSIC ON' : 'MUSIC OFF'
    this.el.musicButton.classList.toggle('off', !on)
  }

  /** Reflect the wall-clock speed multiplier. */
  setSpeed(multiplier: number): void {
    if (!this.el) return
    this.el.speedButton.textContent = `SPEED ${multiplier}x`
    this.el.speedButton.classList.toggle('off', multiplier === 1)
  }


  // --- minimap --------------------------------------------------------------

  private drawMinimap(): void {
    const ctx = this.minimapCtx
    if (!ctx) return
    const image = ctx.createImageData(MINIMAP_SIZE, MINIMAP_SIZE)
    const palette = this.renderer.palette

    for (let cy = 0; cy < MAP_SIZE; cy++) {
      for (let cx = 0; cx < MAP_SIZE; cx++) {
        const cell = cy * MAP_SIZE + cx
        const o = cell * 4
        if (!this.vision.isExplored(this.localSlot, cell)) {
          image.data[o] = 0
          image.data[o + 1] = 0
          image.data[o + 2] = 0
          image.data[o + 3] = 255
          continue
        }
        const kind = this.world.terrain[cell]
        const rampName =
          (this.world.flags[cell] & CELL.Water) !== 0
            ? 'ice'
            : kind === TERRAIN.Gems
              ? 'gems'
              : kind === TERRAIN.Regolith
            ? 'regolith'
            : (this.world.flags[cell] & CELL.Cliff) !== 0
              ? 'rock'
              : 'dust'
        const ramp = palette.terrainRamps[rampName]
        let [r, g, b] = palette.rgb(ramp.indices[ramp.indices.length - 2] ?? ramp.indices[0])
        if (this.world.ground[cell] !== GROUND.Natural) [r, g, b] = [105, 117, 114]
        else if ((this.world.flags[cell] & CELL.Scenery) !== 0) [r, g, b] = [51, 83, 55]
        // Explored-but-not-visible is dimmed, matching the world's fog pass.
        const dim = this.vision.isVisible(this.localSlot, cell) ? 1 : 0.45
        image.data[o] = r * dim
        image.data[o + 1] = g * dim
        image.data[o + 2] = b * dim
        image.data[o + 3] = 255
      }
    }

    // Blips. Enemies appear only where currently visible.
    for (let s = 0; s < this.entities.limit; s++) {
      if (!this.entities.isAlive(s)) continue
      const owner = this.entities.owner[s]
      if (owner < 0) continue
      const cx = this.entities.x[s] >> 16
      const cy = this.entities.y[s] >> 16
      if (cx < 0 || cy < 0 || cx >= MAP_SIZE || cy >= MAP_SIZE) continue
      const cell = cy * MAP_SIZE + cx
      if (owner !== this.localSlot && !this.vision.isVisible(this.localSlot, cell)) continue

      // Per-slot owner ramp, never a faction colour (R34).
      const ramp = palette.ownerRamps[owner % palette.ownerRamps.length]
      const [r, g, b] = palette.rgb(ramp.indices[ramp.indices.length - 1])
      const size = this.entities.has(s, EF.Structure) ? 2 : 1
      for (let dy = 0; dy < size; dy++) {
        for (let dx = 0; dx < size; dx++) {
          const x = cx + dx
          const y = cy + dy
          if (x >= MAP_SIZE || y >= MAP_SIZE) continue
          const o = (y * MAP_SIZE + x) * 4
          image.data[o] = r
          image.data[o + 1] = g
          image.data[o + 2] = b
          image.data[o + 3] = 255
        }
      }
    }

    ctx.putImageData(image, 0, 0)
    this.drawViewportRect(ctx)
  }

  /** The camera rectangle, so a minimap click is navigation rather than guesswork. */
  private drawViewportRect(ctx: CanvasRenderingContext2D): void {
    const layout = this.renderer.getLayout()
    // The corners come from the layout, not from constants. They were 479 and
    // 269 — the old fixed internal target — which stopped being the size of
    // anything the moment the render target started following the window, and
    // the rectangle drifted by the difference.
    const left = layout.rect.x + layout.offsetX + 1
    const top = layout.rect.y + layout.offsetY + 1
    const right = layout.rect.x + layout.offsetX + (layout.internalWidth - 1) * layout.scale
    const bottom = layout.rect.y + layout.offsetY + (layout.internalHeight - 1) * layout.scale

    // All four corners, not two. The screen rectangle maps to a *rotated* quad
    // on the ground, so opposite corners alone collapse the rect to a line.
    const corners = [
      this.renderer.screenToWorld(left, top),
      this.renderer.screenToWorld(right, top),
      this.renderer.screenToWorld(right, bottom),
      this.renderer.screenToWorld(left, bottom),
    ]
    if (corners.some((c) => !c)) return
    const xs = corners.map((c) => c!.x)
    const zs = corners.map((c) => c!.z)
    // World cells to minimap pixels. The two happen to be equal today; naming
    // the conversion means a change to either does not silently skew the box.
    const scale = MINIMAP_SIZE / MAP_SIZE
    ctx.strokeStyle = 'rgba(232, 217, 197, 0.85)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(xs[0] * scale, zs[0] * scale)
    for (let i = 1; i < corners.length; i++) ctx.lineTo(xs[i] * scale, zs[i] * scale)
    ctx.closePath()
    ctx.stroke()
  }

  // --- alerts (R33) ---------------------------------------------------------

  private onRejected(p: { reason: Reason; detail?: string }): void {
    const text =
      p.reason === REASON.QueueFull ? QUEUE_FULL_TEXT : (REASON_TEXT[p.reason as Reason] ?? p.reason)
    this.alert(p.detail ? `${text} (${p.detail})` : text, true)
  }

  alert(text: string, bad = false): void {
    const node = div(bad ? 'ba-alert bad' : 'ba-alert')
    node.textContent = text
    this.el.alerts.append(node)
    // Trim rather than accumulate; the animation fades but does not remove.
    while (this.el.alerts.childElementCount > 5) this.el.alerts.firstElementChild?.remove()
    window.setTimeout(() => node.remove(), 4200)
  }

  dispose(): void {
    this.icons.dispose()
    this.el.sidebar.remove()
    this.el.alerts.remove()
    this.el.offline.remove()
  }
}

function div(className: string): HTMLDivElement {
  const d = document.createElement('div')
  d.className = className
  return d
}
